import { Card, Stack, Text } from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { useTranslation } from "react-i18next";

import { ReferralCodeHero, ReferralShareButtons } from "./ReferralShareButtons";
import { useReferralCode } from "./useReferralCode";
import { useReferralShare } from "./useReferralShare";

export function ReferAndEarn() {
  const { t } = useTranslation();
  const { isFlagEnabled, code, shareUrl } = useReferralCode();
  const {
    copyShareUrl,
    shareNative,
    linkCopied,
    whatsAppHref,
    smsHref,
    mailtoHref,
  } = useReferralShare({ code, shareUrl });

  if (!isFlagEnabled || !code) {
    return null;
  }

  const heading = t("Know someone who needs a background check?");

  return (
    <Card
      type="large"
      border="border-base"
      testID="refer-and-earn"
      ariaLabel={heading}
      className={styles.card}
    >
      <Stack gap="space-5" align="center">
        <Stack gap="space-2" align="center">
          <Text as="h2" style="global/heading-medium" align="center">
            {heading}
          </Text>
          <Text style="global/body-secondary" color="text-weak" align="center">
            {t(
              "Share your referral code with them. They get $5 off, and you get up to $5 back when they complete a background check."
            )}
          </Text>
        </Stack>
        <ReferralCodeShare
          code={code}
          whatsAppHref={whatsAppHref}
          smsHref={smsHref}
          mailtoHref={mailtoHref}
          onCopyLink={copyShareUrl}
          onShareMore={shareNative}
          linkCopied={linkCopied}
        />
      </Stack>
    </Card>
  );
}

function ReferralCodeShare({
  code,
  whatsAppHref,
  smsHref,
  mailtoHref,
  onCopyLink,
  onShareMore,
  linkCopied,
}: {
  code: string;
  whatsAppHref: string;
  smsHref: string;
  mailtoHref: string;
  onCopyLink: () => void;
  onShareMore: () => void;
  linkCopied: boolean;
}) {
  const { t } = useTranslation();

  return (
    <Stack gap="space-5" align="center">
      <Stack gap="space-1" align="center">
        <Text style="input/label" align="center" testID="referral-code-label">
          {t("Referral code")}
        </Text>
        <ReferralCodeHero code={code} testID="referral-code" />
      </Stack>
      <Stack gap="space-2" align="center">
        <ReferralShareButtons
          whatsAppHref={whatsAppHref}
          smsHref={smsHref}
          mailtoHref={mailtoHref}
          onCopyLink={onCopyLink}
          onShareMore={onShareMore}
          linkCopied={linkCopied}
        />
        <Text
          style="global/footer-small"
          color="text-weak"
          align="center"
          testID="referral-refund-footnote"
        >
          {t(
            "Refunds are credited back to the original payment, up to the amount paid."
          )}
        </Text>
      </Stack>
    </Stack>
  );
}

const styles = {
  card: css({
    boxSizing: "border-box",
    padding: "var(--space-5)",
    width: "100%",
  }),
};
